/**
 * The renderer bridge: every push of React/prop state onto the live `CombatBoardHandle`. Each effect
 * is the same shape — gate on `boardReady`, hand the (mostly raw) domain data to a `set*` handle
 * method, let `pixiCombatBoard.ts` resolve the render — so they live together here rather than
 * spread through `<CombatStage>`'s body. Effect ORDER is the source order they had in the component,
 * which is what the one-way "state in, pixels out" contract depends on.
 *
 * The uncommitted authoring ghost is deliberately NOT here: it is driven by local gesture state
 * rather than encounter state, and has its own pure resolver — see `useAuthoringPreviewSync`.
 */
import { useEffect, useRef, type RefObject } from "react";
import type { Annotation, AnnotationColor, AnnotationId, AreaEffect, AreaEffectId, Combatant, CombatantId, EncounterGround, MovePathPreview, Room, RoomId, TerrainMap, EdgeMap } from "@lancer-vtt/domain";
import type { CellSightline } from "../../../lib/encounter/combatInspector";
import type { ThreatRingsView } from "../../../lib/encounter/hoverIntel";
import type { FloatItem, ProjectileItem, ImpactItem } from "../../../lib/encounter/usePresentationDirector";
import type { CombatBoardHandle, DrawPreviewSpec, SitrepZoneRender } from "../pixiCombatBoard";
import type { TokenImageCtx } from "../../../lib/tokenArt";
import type { TokenChrome } from "../../../lib/encounter/tokenChrome";
import type { DrawPoint } from "../combatDrawing";
import { droneAuraRings } from "../../../lib/encounter/droneAuras";
import { FLOAT_LOG_CAP } from "./constants";
import { CELL, asSquareCell, footprintBox } from "../combatCells";
import type { View } from "./types";

/** No hover-intel rings: what the board draws while none are shown. */
const NO_RINGS: ThreatRingsView = { rings: [], cover: new Map() };

export function useBoardSync({
  boardReady,
  boardHandleRef,
  view,
  setView,
  size,
  cols,
  rows,
  terrain,
  edges,
  ground,
  rooms,
  sitrepZones,
  areaEffects,
  combatants,
  selectedId,
  viewerSlot,
  tokenImageCtx,
  tokenChrome,
  outOfRangeTargetIds,
  reachable,
  boostRange,
  sensorRange,
  threatRings,
  movePath,
  movePathRefused,
  movePreviews,
  cellSightlines,
  attackHover,
  attackLosExcludedHover,
  pendingDeploy,
  annotations,
  selectedAnnotationId,
  freehandPreview,
  polylinePreview,
  polylineCursor,
  drawColor,
  fullGrid,
  terrainMechanics,
  terrainMechanicsFocus,
  cursors,
  ping,
  focusPoint,
  floats,
  onFloatDone,
  floatLogRef,
  projectiles,
  onProjectileDone,
  impacts,
  onImpactDone,
  chainFocus,
  chainFocusLogRef,
}: {
  boardReady: boolean;
  boardHandleRef: RefObject<CombatBoardHandle | null>;
  view: View;
  setView: (next: View | ((v: View) => View)) => void;
  size: { width: number; height: number };
  cols: number;
  rows: number;
  terrain: TerrainMap;
  edges: EdgeMap;
  ground: EncounterGround;
  rooms: Record<RoomId, Room>;
  sitrepZones: SitrepZoneRender[];
  areaEffects: Record<AreaEffectId, AreaEffect>;
  combatants: Combatant[];
  selectedId: string | null;
  /** The viewing slot — the token layer rings the units it controls (G-D7). */
  viewerSlot: string | null;
  tokenImageCtx: TokenImageCtx;
  /** Each token's turn state + vitals, already projected for this viewer (`tokenChromeFor`). */
  tokenChrome: Record<CombatantId, TokenChrome>;
  outOfRangeTargetIds: Set<string> | null;
  reachable: Set<string> | null;
  boostRange: Set<string> | null;
  sensorRange: Set<string> | null;
  threatRings: ThreatRingsView | null;
  movePath: MovePathPreview | null;
  /** The drag trail is one the drop would refuse (a Trampler's, over the move left): drawn red. */
  movePathRefused: boolean;
  movePreviews: { cells: string[]; by: string }[] | undefined;
  cellSightlines: CellSightline[] | null;
  attackHover: Set<string> | null;
  attackLosExcludedHover: Set<string> | null;
  pendingDeploy: { cells: string[]; size: number } | null;
  annotations: Record<AnnotationId, Annotation>;
  selectedAnnotationId: AnnotationId | null;
  freehandPreview: DrawPoint[] | null;
  polylinePreview: DrawPoint[] | null;
  polylineCursor: DrawPoint | null;
  drawColor: AnnotationColor;
  fullGrid: boolean;
  terrainMechanics: boolean;
  /** The Inspect tool's cell key, whose mechanics pieces show even with the overlay off. */
  terrainMechanicsFocus: string | null;
  cursors: { at: { x: number; y: number }; by: string; label: string }[] | undefined;
  ping: { at: { x: number; y: number }; by: string; seq: number } | null | undefined;
  focusPoint: { x: number; y: number; seq: number } | null | undefined;
  floats: FloatItem[];
  onFloatDone: (key: number) => void;
  floatLogRef: RefObject<{ text: string; at: number }[]>;
  projectiles: ProjectileItem[];
  onProjectileDone: (key: number) => void;
  impacts: ImpactItem[];
  onImpactDone: (key: number) => void;
  /** The tokens the chain link now playing rings (`usePresentationDirector`'s `focus`), `[]` for none. */
  chainFocus: readonly string[];
  /** Every focus change, for `__combatBoardDebug.getChainFocusLog()`. */
  chainFocusLogRef: RefObject<{ ids: string[]; at: number }[]>;
}) {
  // Received pings → a transient burst on the Pixi VFX layer (`pixiCombatBoard.ts`'s `addPing`, §3 row
  // 13 — self-removing, no React-side lifecycle needed anymore). Keyed by the broadcast's monotonic
  // `seq` so a flurry of pings each animate independently and a repeat at the same spot re-flashes.
  // This ref tracks "last seq we've SEEN," not "last seq we drew," so a ping that arrives before the
  // board mounts re-fires once it's ready.
  const lastPingSeqRef = useRef<number | null>(null);
  // The last-seen `focusPoint.seq` (same "seen," not "drawn," shape as `lastPingSeqRef` just above).
  const lastFocusSeqRef = useRef<number | null>(null);

  // Pan/zoom (§4's pan/zoom row): push `view` onto the world container's `.scale`/`.position`
  // whenever it changes, plus once more as soon as the handle exists (it may already have changed
  // once — e.g. the initial centering effect — before the async mount resolved).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setView(view);
  }, [boardReady, view, boardHandleRef]);

  // Board background (§3 row 1) — rebuilt only when the grid extent changes.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setBoard(cols, rows);
  }, [boardReady, cols, rows, boardHandleRef]);

  // Terrain/edges (§3 row 3).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setTerrain(terrain, edges, ground);
  }, [boardReady, terrain, edges, ground, boardHandleRef]);

  // The PLAY full grid toggle — a layer's visibility, never a terrain repaint.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setFullGrid(fullGrid);
  }, [boardReady, fullGrid, boardHandleRef]);

  // The terrain mechanics overlay (the rail's VIEW toggle O); the board redraws it on a terrain change.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setTerrainMechanics(terrainMechanics);
  }, [boardReady, terrainMechanics, boardHandleRef]);
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setTerrainMechanicsFocus(terrainMechanicsFocus);
  }, [boardReady, terrainMechanicsFocus, boardHandleRef]);

  // Rooms (§3 row 2) — render-only; the dblclick-to-inspect hit-test lives in `onStageDblClick`.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setRooms(rooms);
  }, [boardReady, rooms, boardHandleRef]);

  // Sitrep zones (§3 row 3.6) — render-only.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setSitrepZones(sitrepZones);
  }, [boardReady, sitrepZones, boardHandleRef]);

  // Area-effect clouds (§3 row 2.5) — render-only; cover itself is a
  // live server-side query (D4), this just draws wherever a live `AreaEffect` record currently is.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setAreaEffects(areaEffects);
  }, [boardReady, areaEffects, boardHandleRef]);

  // Drone auras (`ll1-drones` 9.1) — render-only, a ring per live aura drone.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setDroneAuras(droneAuraRings(combatants));
  }, [boardReady, combatants, boardHandleRef]);

  // ── Tokens + the movement/attack overlays + annotations + cursors/pings ─────────────────────────
  //
  // Tokens (§3 row 9) + name labels (row 10) + the selection halo (row 8) rebuild together since the
  // halo/label geometry both key off the same token set.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setTokens(combatants, { selectedId, untargetableIds: outOfRangeTargetIds ?? null, viewerSlot, imageCtx: tokenImageCtx, chrome: tokenChrome });
  }, [boardReady, combatants, selectedId, outOfRangeTargetIds, viewerSlot, tokenImageCtx, tokenChrome, boardHandleRef]);

  // Movement-range wash (§3 row 4) — the selected unit's reachable footprint cells.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setMoveRange(reachable ?? new Set(), cols, rows);
  }, [boardReady, reachable, cols, rows, boardHandleRef]);
  // The boost ring beside it (v20): cells only a Boost reaches. The stage passes it only while a
  // PLAY drag is past base reach (`combat-surface-polish` R5).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setBoostRange(boostRange ?? new Set(), cols, rows);
  }, [boardReady, boostRange, cols, rows, boardHandleRef]);

  // Sensors (TECH-RANGE) reach wash — the armed attacker's Sensors
  // range, range-only, while any Sensors-scoped aim is armed (search today; tech aim modes join
  // later without reshaping this effect). Same shape as the move-range push above.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setSensorRange(sensorRange ?? new Set(), cols, rows);
  }, [boardReady, sensorRange, cols, rows, boardHandleRef]);

  // Hover intel's weapon rings, over the move wash (which carries the hovered unit's move zone).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setThreatRings(threatRings ?? NO_RINGS, cols, rows);
  }, [boardReady, threatRings, cols, rows, boardHandleRef]);

  // Live move-path preview (§3 row 5) — the cells OUR own in-progress token drag would sweep;
  // `movePath` is set directly by the native-DOM drag handlers (3.2).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setMovePathRefused(movePathRefused);
    boardHandleRef.current?.setMovePath(movePath ?? { cells: new Set(), boostCells: new Set() }, cols, rows);
  }, [boardReady, movePath, movePathRefused, cols, rows, boardHandleRef]);

  // Remote move-path ghosts (§3 row 6) — every OTHER participant's in-progress drag trail.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setRemoteMovePaths(movePreviews ?? [], cols, rows);
  }, [boardReady, movePreviews, cols, rows, boardHandleRef]);

  // Attack reach wash + the hovered AoE template (§3 row 7), plus its LoS-excluded markers.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setTargeting(cellSightlines, { attackAffected: attackHover, attackLosExcluded: attackLosExcludedHover, cols, rows });
  }, [boardReady, cellSightlines, attackHover, attackLosExcludedHover, cols, rows, boardHandleRef]);

  // Pending directed-deploy section marks (`jericho-directed-placement` D1 follow-up) — one translucent silhouette per
  // cell already picked in a live `activate_sites` gesture, so a section shows up AS its cell is
  // committed rather than both landing at once on the final pick.
  //
  // **The teardown is structural, not enumerated.** `pendingDeploy` is derived from `aim`, so every
  // way the gesture can end — the final pick firing the intent, the panel's `×` cancel, Esc, the
  // back-one-pick control, the actor's turn ending, the socket dropping and re-welcoming a fresh
  // state, this component unmounting — all reduce to the SAME thing: `aim` stops being an
  // `activate_sites` draft, the prop goes `null`, this effect re-runs and clears the layer. Nothing
  // here enumerates exit paths, so a seventh one added later is covered for free. (Deliberately not
  // a `useEffect` cleanup-function teardown: the cleanup only fires on a dep change/unmount, which is
  // the same moment the redraw would run anyway — clearing wholesale inside `setPendingDeploy` keeps
  // one rebuild contract instead of two half-teardowns that can disagree.)
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setPendingDeploy(pendingDeploy?.cells ?? null, { size: pendingDeploy?.size ?? 1, cols, rows });
  }, [boardReady, pendingDeploy, cols, rows, boardHandleRef]);

  // Persisted draw annotations (§3 row 11) — selection state (`selectedAnnotationId`) is local UI
  // state (never synced), so it's read straight off state here.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setAnnotations(annotations, selectedAnnotationId);
  }, [boardReady, annotations, selectedAnnotationId, boardHandleRef]);

  // The in-progress draw preview (§3 row 12) — an open freehand drag and/or an accumulating polyline.
  useEffect(() => {
    if (!boardReady) return;
    const spec: DrawPreviewSpec | null =
      freehandPreview || polylinePreview ? { color: drawColor, freehandPreview, polylinePreview, polylineCursor } : null;
    boardHandleRef.current?.setDrawPreview(spec);
  }, [boardReady, freehandPreview, polylinePreview, polylineCursor, drawColor, boardHandleRef]);

  // Remote cursors (§3 row 15).
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setCursors(cursors ?? []);
  }, [boardReady, cursors, boardHandleRef]);

  // Received map pings (§3 row 13) — see `lastPingSeqRef`'s declaration above for why the "have we
  // SEEN this seq" bookkeeping lives there while the actual draw is gated here on `boardReady`.
  useEffect(() => {
    if (!boardReady || !ping || ping.seq === lastPingSeqRef.current) return;
    lastPingSeqRef.current = ping.seq;
    boardHandleRef.current?.addPing(ping.at, ping.by);
  }, [boardReady, ping, boardHandleRef]);

  // Camera focus request (`combat-chip-camera-focus`) — pans the view so `focusPoint` centers in the
  // viewport, at whatever scale the viewer already had (never re-zooms). An instant re-center, not a
  // tween: the board has no existing view-transform tween utility to reuse (only Pixi-node tweens,
  // `pixiAnimator.ts`'s `tween()`, which animate a token container's own x/y/alpha — a different thing
  // from the world-root pan/zoom transform this sets). Mirrors the initial "drop the board in its
  // center" effect in the component, but centering an arbitrary point instead of the whole board rect.
  useEffect(() => {
    if (!boardReady || !focusPoint || focusPoint.seq === lastFocusSeqRef.current) return;
    lastFocusSeqRef.current = focusPoint.seq;
    setView((v) => ({
      ...v,
      x: Math.round(size.width / 2 - focusPoint.x * v.scale),
      y: Math.round(size.height / 2 - focusPoint.y * v.scale),
    }));
  }, [boardReady, focusPoint, size, setView]);

  // The animator's float/projectile/impact VFX sinks — `usePresentationDirector`'s
  // `floats`/`projectiles`/`impacts` arrays are the
  // renderer-agnostic bridge: `pixiAnimator.ts`'s sinks push an item here. Each item is handed to `pixiCombatBoard.ts` (which owns its
  // full rise/travel/burst lifecycle, ticker-driven and self-removing — the same pattern `addPing`
  // already uses) and immediately dropped from React state via `onFloatDone`/etc; nothing here re-reads
  // an item once it's spawned, so handing off + dropping in the same pass is safe (a later item simply
  // arrives as a new array entry).
  useEffect(() => {
    if (!boardReady) return;
    for (const f of floats) {
      boardHandleRef.current?.spawnFloat(f);
      // Timestamp the disclosure for `__combatBoardDebug.getFloatLog()` — see its doc. Capped so a
      // long session can't grow this without bound; a flow only ever reads the last few.
      floatLogRef.current.push({ text: f.text, at: performance.now() });
      if (floatLogRef.current.length > FLOAT_LOG_CAP) floatLogRef.current.shift();
      onFloatDone(f.key);
    }
  }, [boardReady, floats, onFloatDone, boardHandleRef, floatLogRef]);

  useEffect(() => {
    if (!boardReady) return;
    for (const p of projectiles) {
      boardHandleRef.current?.spawnProjectile(p);
      onProjectileDone(p.key);
    }
  }, [boardReady, projectiles, onProjectileDone, boardHandleRef]);

  // The chain link's focus ring (`chained-resolution-sequence` 2.2, `decisions/0210` item 8): ring
  // the link's actor and target, and pan only when the actor is off screen. An instant re-center,
  // as `focusPoint` above does; no zoom.
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setFocus(chainFocus);
    chainFocusLogRef.current.push({ ids: [...chainFocus], at: performance.now() });
    if (chainFocusLogRef.current.length > FLOAT_LOG_CAP) chainFocusLogRef.current.shift();
    const actor = combatants.find((c) => c.id === chainFocus[0]);
    if (actor?.pos == null) return;
    const { minX, minY, n } = footprintBox(asSquareCell(actor.pos), actor.base.size);
    const world = { x: (minX + n / 2) * CELL, y: (minY + n / 2) * CELL };
    setView((v) => {
      const sx = world.x * v.scale + v.x;
      const sy = world.y * v.scale + v.y;
      const margin = CELL * v.scale;
      const onScreen = sx >= margin && sx <= size.width - margin && sy >= margin && sy <= size.height - margin;
      return onScreen ? v : { ...v, x: Math.round(size.width / 2 - world.x * v.scale), y: Math.round(size.height / 2 - world.y * v.scale) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the focus alone: the actor's
    // later moves, the viewport and the combatant list changing must not re-pan a view the GM has
    // since moved by hand.
  }, [boardReady, chainFocus]);

  useEffect(() => {
    if (!boardReady) return;
    for (const i of impacts) {
      boardHandleRef.current?.spawnImpact(i);
      onImpactDone(i.key);
    }
  }, [boardReady, impacts, onImpactDone, boardHandleRef]);
}
